import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, IconButton, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Card, CardContent, Tooltip, Checkbox, InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, Close as CloseIcon,
  AccountBalance as AccountBalanceIcon, Payment as PaymentIcon,
  AutoAwesome as GenerateIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';

import PatientAutocomplete from '../../components/PatientAutocomplete';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

interface ProfessionalFee {
  id: string;
  professional_id: string;
  professional_name: string;
  patient_id: string | null;
  patient_name: string;
  procedure_name: string;
  description: string;
  gross_amount: number;
  fee_percentage: number;
  fee_amount: number;
  deductions: number;
  net_amount: number;
  payment_status: string;
  reference_month: number;
  reference_year: number;
  notes: string;
}

interface Summary {
  total_gross: number;
  total_fees: number;
  total_paid: number;
  total_pending: number;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendente' },
  { value: 'approved', label: 'Aprovado' },
  { value: 'paid', label: 'Pago' },
  { value: 'cancelled', label: 'Cancelado' },
];

const MONTHS = [
  { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' }, { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' }, { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' }, { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' },
];

const statusColor = (s: string): 'warning' | 'info' | 'success' | 'error' | 'default' => {
  switch (s) {
    case 'pending': return 'warning';
    case 'approved': return 'info';
    case 'paid': return 'success';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

const statusLabel = (s: string) => {
  switch (s) {
    case 'pending': return 'Pendente';
    case 'approved': return 'Aprovado';
    case 'paid': return 'Pago';
    case 'cancelled': return 'Cancelado';
    default: return s;
  }
};

const emptyForm = {
  professional_id: '',
  patient_id: '',
  procedure_name: '',
  description: '',
  gross_amount: '',
  fee_percentage: '',
  fee_amount: '',
  deductions: '0',
  reference_month: String(new Date().getMonth() + 1),
  reference_year: String(new Date().getFullYear()),
  notes: '',
};

const ProfessionalFees: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [rows, setRows] = useState<ProfessionalFee[]>([]);
  const [summary, setSummary] = useState<Summary>({ total_gross: 0, total_fees: 0, total_paid: 0, total_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });

  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof emptyForm>({ ...emptyForm });

  const [generateDialog, setGenerateDialog] = useState(false);
  const [genMonth, setGenMonth] = useState(new Date().getMonth() + 1);
  const [genYear, setGenYear] = useState(new Date().getFullYear());
  const [genProfessional, setGenProfessional] = useState('');
  const [genFeePercentage, setGenFeePercentage] = useState('');
  const [generating, setGenerating] = useState(false);

  const [payDialog, setPayDialog] = useState(false);
  const [payMethod, setPayMethod] = useState('pix');
  const [payReference, setPayReference] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; full_name: string; cpf?: string; phone?: string; email?: string } | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: page + 1, limit, reference_month: filterMonth, reference_year: filterYear };
      if (filterProfessional) params.professional_id = filterProfessional;
      if (filterStatus !== 'all') params.payment_status = filterStatus;

      const { data } = await axios.get(getApiUrl('/financial-module/professional-fees'), { params });
      const payload = data.data || data;
      setRows(payload.rows || payload.items || payload || []);
      setTotal(payload.total || 0);
      setSummary(payload.summary || { total_gross: 0, total_fees: 0, total_paid: 0, total_pending: 0 });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao carregar honorários', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [page, limit, filterMonth, filterYear]);

  const handleFilter = () => { setPage(0); loadData(); };

  const recalcFee = (field: string, value: string, current: any) => {
    const updated = { ...current, [field]: value };
    const gross = parseFloat(updated.gross_amount) || 0;
    const pct = parseFloat(updated.fee_percentage) || 0;
    updated.fee_amount = ((gross * pct) / 100).toFixed(2);
    return updated;
  };

  const handleFormChange = (field: string, value: string) => {
    if (field === 'gross_amount' || field === 'fee_percentage') {
      setForm((prev) => recalcFee(field, value, prev));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setSelectedPatient(null);
    setOpenDialog(true);
  };

  const openEdit = (row: ProfessionalFee) => {
    setEditingId(row.id);
    setForm({
      professional_id: row.professional_id || '',
      patient_id: row.patient_id || '',
      procedure_name: row.procedure_name || '',
      description: row.description || '',
      gross_amount: String(row.gross_amount || ''),
      fee_percentage: String(row.fee_percentage || ''),
      fee_amount: String(row.fee_amount || ''),
      deductions: String(row.deductions || '0'),
      reference_month: String(row.reference_month),
      reference_year: String(row.reference_year),
      notes: row.notes || '',
    });
    setSelectedPatient(
      row.patient_id ? { id: row.patient_id, full_name: row.patient_name || '' } : null
    );
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      const gross = parseFloat(form.gross_amount) || 0;
      const feeAmt = parseFloat(form.fee_amount) || 0;
      const deductions = parseFloat(form.deductions) || 0;

      const body = {
        ...form,
        gross_amount: gross,
        fee_percentage: parseFloat(form.fee_percentage) || 0,
        fee_amount: feeAmt,
        deductions,
        net_amount: feeAmt - deductions,
        reference_month: parseInt(form.reference_month),
        reference_year: parseInt(form.reference_year),
        patient_id: selectedPatient?.id || null,
      };

      if (editingId) {
        await axios.put(getApiUrl(`/financial-module/professional-fees/${editingId}`), body);
        setSnackbar({ open: true, message: 'Honorário atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/financial-module/professional-fees'), body);
        setSnackbar({ open: true, message: 'Honorário criado!', severity: 'success' });
      }
      setOpenDialog(false);
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao salvar', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir este honorário?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/professional-fees/${id}`));
      setSnackbar({ open: true, message: 'Excluído com sucesso!', severity: 'success' });
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao excluir', severity: 'error' });
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const body: Record<string, any> = {
        reference_month: genMonth,
        reference_year: genYear,
        fee_percentage: parseFloat(genFeePercentage) || 0,
      };
      if (genProfessional) body.professional_id = genProfessional;

      const { data } = await axios.post(getApiUrl('/financial-module/professional-fees/generate'), body);
      const result = data.data || data;
      setSnackbar({
        open: true,
        message: `${result.generated || 0} honorários gerados, ${result.existing || 0} já existentes`,
        severity: 'info',
      });
      setGenerateDialog(false);
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao gerar honorários', severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handlePaySelected = async () => {
    try {
      await axios.post(getApiUrl('/financial-module/professional-fees/pay'), {
        ids: selectedIds,
        payment_method: payMethod,
        payment_reference: payReference,
      });
      setSnackbar({ open: true, message: 'Pagamento registrado com sucesso!', severity: 'success' });
      setPayDialog(false);
      setSelectedIds([]);
      setPayMethod('pix');
      setPayReference('');
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao pagar', severity: 'error' });
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const pendingRows = rows.filter((r) => r.payment_status === 'pending');
  const allPendingSelected = pendingRows.length > 0 && pendingRows.every((r) => selectedIds.includes(r.id));

  const toggleSelectAll = () => {
    if (allPendingSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingRows.map((r) => r.id));
    }
  };

  const computedNetAmount = () => {
    const fee = parseFloat(form.fee_amount) || 0;
    const ded = parseFloat(form.deductions) || 0;
    return formatCurrency(fee - ded);
  };

  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <Box sx={pageContainerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={700}>Honorários</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>Atualizar</Button>
          <Button variant="outlined" color="secondary" startIcon={<GenerateIcon />} onClick={() => setGenerateDialog(true)}>
            Gerar Honorários
          </Button>
          {selectedIds.length > 0 && (
            <Button variant="contained" color="success" startIcon={<PaymentIcon />}
              onClick={() => { setPayMethod('pix'); setPayReference(''); setPayDialog(true); }}>
              Pagar Selecionados ({selectedIds.length})
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Novo Honorário</Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Bruto', value: summary.total_gross, color: '#1565c0', bg: '#e3f2fd' },
          { label: 'Total Honorários', value: summary.total_fees, color: '#6a1b9a', bg: '#f3e5f5' },
          { label: 'Total Pago', value: summary.total_paid, color: '#2e7d32', bg: '#e8f5e9' },
          { label: 'Total Pendente', value: summary.total_pending, color: '#e65100', bg: '#fff3e0' },
        ].map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.label}>
            <Card sx={{ backgroundColor: c.bg }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: c.color }}>{formatCurrency(c.value)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Mês</InputLabel>
              <Select value={filterMonth} label="Mês" onChange={(e) => setFilterMonth(Number(e.target.value))}>
                {MONTHS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Ano</InputLabel>
              <Select value={filterYear} label="Ano" onChange={(e) => setFilterYear(Number(e.target.value))}>
                {yearOptions.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Profissional (ID)" value={filterProfessional}
              onChange={(e) => setFilterProfessional(e.target.value)} placeholder="Opcional" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                {STATUS_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button fullWidth variant="contained" onClick={handleFilter}>Filtrar</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#f5f5f5' } }}>
                  <TableCell padding="checkbox">
                    <Checkbox checked={allPendingSelected} indeterminate={selectedIds.length > 0 && !allPendingSelected}
                      onChange={toggleSelectAll} disabled={pendingRows.length === 0} />
                  </TableCell>
                  <TableCell>Profissional</TableCell>
                  <TableCell>Paciente</TableCell>
                  <TableCell>Procedimento</TableCell>
                  <TableCell align="right">Valor Bruto</TableCell>
                  <TableCell align="right">Honorário</TableCell>
                  <TableCell align="right">%</TableCell>
                  <TableCell align="right">Deduções</TableCell>
                  <TableCell align="right">Líquido</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Nenhum honorário encontrado</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r.id} hover selected={selectedIds.includes(r.id)}>
                      <TableCell padding="checkbox">
                        {r.payment_status === 'pending' && (
                          <Checkbox checked={selectedIds.includes(r.id)} onChange={() => toggleSelect(r.id)} />
                        )}
                      </TableCell>
                      <TableCell>{r.professional_name || r.professional_id}</TableCell>
                      <TableCell>{r.patient_name || r.patient_id || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.procedure_name || '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(r.gross_amount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(r.fee_amount)}</TableCell>
                      <TableCell align="right">{(r.fee_percentage || 0).toFixed(1)}%</TableCell>
                      <TableCell align="right">{formatCurrency(r.deductions)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(r.net_amount)}</TableCell>
                      <TableCell align="center">
                        <Chip label={statusLabel(r.payment_status)} color={statusColor(r.payment_status)} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={() => openEdit(r)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Linhas por página"
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog fullScreen={isMobile} scroll="paper" open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingId ? 'Editar Honorário' : 'Novo Honorário'}
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Profissional (ID)" value={form.professional_id} required
                onChange={(e) => handleFormChange('professional_id', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <PatientAutocomplete
                value={selectedPatient}
                onChange={(p) => {
                  setSelectedPatient(p);
                  setForm((prev) => ({ ...prev, patient_id: p?.id || '' }));
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Procedimento" value={form.procedure_name}
                onChange={(e) => handleFormChange('procedure_name', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Descrição" value={form.description}
                onChange={(e) => handleFormChange('description', e.target.value)} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Valor Bruto" type="number" value={form.gross_amount}
                onChange={(e) => handleFormChange('gross_amount', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="% Honorário" type="number" value={form.fee_percentage}
                onChange={(e) => handleFormChange('fee_percentage', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Valor Honorário" type="number" value={form.fee_amount} disabled
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Deduções" type="number" value={form.deductions}
                onChange={(e) => handleFormChange('deductions', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Líquido:</Typography>
              <Typography variant="body1" fontWeight={600}>{computedNetAmount()}</Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Mês Ref.</InputLabel>
                <Select value={form.reference_month} label="Mês Ref." onChange={(e) => handleFormChange('reference_month', e.target.value)}>
                  {MONTHS.map((m) => <MenuItem key={m.value} value={String(m.value)}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Ano Ref.</InputLabel>
                <Select value={form.reference_year} label="Ano Ref." onChange={(e) => handleFormChange('reference_year', e.target.value)}>
                  {yearOptions.map((y) => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observações" multiline rows={2} value={form.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.professional_id || !form.gross_amount}>
            {editingId ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Dialog */}
      <Dialog fullScreen={isMobile} scroll="paper" open={generateDialog} onClose={() => setGenerateDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Gerar Honorários
          <IconButton onClick={() => setGenerateDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Mês</InputLabel>
                <Select value={genMonth} label="Mês" onChange={(e) => setGenMonth(Number(e.target.value))}>
                  {MONTHS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Ano</InputLabel>
                <Select value={genYear} label="Ano" onChange={(e) => setGenYear(Number(e.target.value))}>
                  {yearOptions.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Profissional (ID)" value={genProfessional}
                onChange={(e) => setGenProfessional(e.target.value)} placeholder="Vazio = todos" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="% Honorário" type="number" value={genFeePercentage}
                onChange={(e) => setGenFeePercentage(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={handleGenerate} startIcon={<GenerateIcon />}
            disabled={generating || !genFeePercentage}>
            {generating ? <CircularProgress size={20} /> : 'Gerar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pay Selected Dialog */}
      <Dialog fullScreen={isMobile} scroll="paper" open={payDialog} onClose={() => setPayDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Pagar Selecionados ({selectedIds.length})
          <IconButton onClick={() => setPayDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Método Pagamento</InputLabel>
                <Select value={payMethod} label="Método Pagamento" onChange={(e) => setPayMethod(e.target.value)}>
                  <MenuItem value="pix">PIX</MenuItem>
                  <MenuItem value="transfer">Transferência</MenuItem>
                  <MenuItem value="cash">Dinheiro</MenuItem>
                  <MenuItem value="check">Cheque</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Referência Pagamento" value={payReference}
                onChange={(e) => setPayReference(e.target.value)} placeholder="Comprovante, nº transação, etc." />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handlePaySelected} startIcon={<PaymentIcon />}>
            Confirmar Pagamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfessionalFees;
